// AdvancedComprehensiveChart.jsx
import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { Card, Spin, Tabs, Row, Col, Statistic } from 'antd';
import './index.css';

const { TabPane } = Tabs;

const AdvancedComprehensiveChart = ({
    dashboardData,
    orderData,
    loading,
    dateFrom,
    dateTo
}) => {
    const [activeTab, setActiveTab] = useState('overview');

    // Process comprehensive data for all chart types
    const processComprehensiveData = () => {
        if (!orderData || !orderData.length) return [];

        const dailyData = orderData.reduce((acc, order) => {
            const orderDate = order.OrderDateTime ? order.OrderDateTime.split('T')[0] : 'Unknown';

            if (!acc[orderDate]) {
                acc[orderDate] = {
                    date: orderDate,
                    // Revenue Metrics
                    totalOrders: 0,
                    totalRevenue: 0,
                    totalTax: 0,
                    totalDeliveryCharges: 0,
                    totalDiscount: 0,
                    averageOrderValue: 0,

                    // Order Status
                    confirmedOrders: 0,
                    // cancelledOrders: 0,
                    paidOrders: 0,
                    pendingOrders: 0,

                    // Order Sources
                    webOrders: 0,
                    callOrders: 0,
                    posOrders: 0,

                    // Order Modes
                    deliveryOrders: 0,
                    dineInOrders: 0,
                    pickupOrders: 0,

                    // Payment Methods
                    cashPayments: 0,
                    cardPayments: 0,
                    otherPayments: 0,

                    // Product Categories (from order details)
                    beefBurgers: 0,
                    chickenBurgers: 0,
                    fries: 0,
                    drinks: 0,
                    upgrades: 0
                };
            }

            // Basic metrics
            acc[orderDate].totalOrders += 1;
            acc[orderDate].totalRevenue += order.NetAmount || 0;
            acc[orderDate].totalTax += order.Gst || 0;
            acc[orderDate].totalDeliveryCharges += order.DeliveryCharges || 0;
            acc[orderDate].totalDiscount += order.Discount || 0;

            // Status tracking
            if (order.OrderStatus === 'Confirmed') acc[orderDate].confirmedOrders += 1;
            // if (order.OrderStatus === 'Cancel') acc[orderDate].cancelledOrders += 1;
            if (order.OrderStatus === 'Paid') acc[orderDate].paidOrders += 1;
            if (order.OrderStatus === 'Pending') acc[orderDate].pendingOrders += 1;

            // Source tracking
            if (order.OrderSource === 'Web') acc[orderDate].webOrders += 1;
            if (order.OrderSource === 'Call') acc[orderDate].callOrders += 1;
            if (order.OrderSource === 'POS') acc[orderDate].posOrders += 1;

            // Mode tracking
            if (order.OrderMode === 'Delivery') acc[orderDate].deliveryOrders += 1;
            if (order.OrderMode === 'DineIn') acc[orderDate].dineInOrders += 1;
            if (order.OrderMode === 'Pickup') acc[orderDate].pickupOrders += 1;

            // Payment tracking
            if (order.PaymentMode === 'CASH') acc[orderDate].cashPayments += 1;
            if (order.PaymentMode === 'CARD') acc[orderDate].cardPayments += 1;
            if (!order.PaymentMode) acc[orderDate].otherPayments += 1;

            // Category tracking from order details
            order.OrderDetails?.forEach(detail => {
                const category = detail.CategoryName;
                if (category?.includes('Beef')) acc[orderDate].beefBurgers += detail.Quantity || 0;
                if (category?.includes('Chicken')) acc[orderDate].chickenBurgers += detail.Quantity || 0;
                if (category?.includes('Fries')) acc[orderDate].fries += detail.Quantity || 0;
                if (category?.includes('Drink') || category?.includes('Beverage')) acc[orderDate].drinks += detail.Quantity || 0;
                if (category?.includes('Upgrade')) acc[orderDate].upgrades += detail.Quantity || 0;
            });

            return acc;
        }, {});

        // Calculate average order value and convert to array
        const result = Object.values(dailyData).map(day => ({
            ...day,
            averageOrderValue: day.totalOrders > 0 ? day.totalRevenue / day.totalOrders : 0
        }));

        return result.sort((a, b) => new Date(a.date) - new Date(b.date)).slice(-30);
    };

    // Process data for pie charts
    const processSummaryData = () => {
        const chartData = processComprehensiveData();
        const totalData = chartData.reduce((acc, day) => ({
            totalRevenue: acc.totalRevenue + day.totalRevenue,
            totalOrders: acc.totalOrders + day.totalOrders,
            confirmedOrders: acc.confirmedOrders + day.confirmedOrders,
            // cancelledOrders: acc.cancelledOrders + day.cancelledOrders,
            paidOrders: acc.paidOrders + day.paidOrders,
            webOrders: acc.webOrders + day.webOrders,
            callOrders: acc.callOrders + day.callOrders,
            deliveryOrders: acc.deliveryOrders + day.deliveryOrders,
            dineInOrders: acc.dineInOrders + day.dineInOrders,
            cashPayments: acc.cashPayments + day.cashPayments,
            cardPayments: acc.cardPayments + day.cardPayments,
        }), {
            totalRevenue: 0, totalOrders: 0, confirmedOrders: 0,
            paidOrders: 0, webOrders: 0, callOrders: 0, deliveryOrders: 0,
            dineInOrders: 0, cashPayments: 0, cardPayments: 0
        });

        return {
            orderStatus: [
                { name: 'Confirmed', value: totalData.confirmedOrders, color: '#1890ff' },
                { name: 'Paid', value: totalData.paidOrders, color: '#52c41a' },
                // { name: 'Cancelled', value: totalData.cancelledOrders, color: '#ff4d4f' },
            ],
            orderSource: [
                { name: 'Web', value: totalData.webOrders, color: '#1890ff' },
                { name: 'Call', value: totalData.callOrders, color: '#faad14' },
                { name: 'POS', value: totalData.posOrders, color: '#13c2c2' },
            ],
            orderMode: [
                { name: 'Delivery', value: totalData.deliveryOrders, color: '#722ed1' },
                { name: 'Dine In', value: totalData.dineInOrders, color: '#fa541c' },
                { name: 'Pickup', value: totalData.pickupOrders, color: '#eb2f96' },
            ]
        };
    };

    const chartData = processComprehensiveData();
    const summaryData = processSummaryData();

    const formatTooltipValue = (value, name) => {
        if (name.includes('Revenue') || name.includes('Tax') || name.includes('Charges') || name.includes('Discount')) {
            return [`Rs. ${value?.toLocaleString('en-PK') || '0'}`, name];
        }
        return [value, name];
    };

    if (loading) {
        return (
            <Card className="mb-6">
                <div className="flex justify-center items-center h-64">
                    <Spin size="large" />
                </div>
            </Card>
        );
    }

    return (
        <Card className="mb-6 shadow-sm">
            <Tabs className='my-tabs' activeKey={activeTab} onChange={setActiveTab}>
                <TabPane tab="Business Overview" key="overview">
                    <div className="h-96">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                />
                                <YAxis yAxisId="left" />
                                <YAxis yAxisId="right" orientation="right" />
                                <Tooltip formatter={formatTooltipValue} />
                                <Legend />
                                <Line yAxisId="left" dataKey="totalRevenue" stroke="#1890ff" name="Revenue" strokeWidth={3} />
                                <Line yAxisId="right" dataKey="totalOrders" stroke="#52c41a" name="Orders" />
                                <Line yAxisId="left" dataKey="averageOrderValue" stroke="#fa541c" name="Avg Order Value" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </TabPane>

                <TabPane tab="Order Analytics" key="orders">
                    <div className="h-96">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="confirmedOrders" fill="#1890ff" name="Confirmed" />
                                <Bar dataKey="paidOrders" fill="#52c41a" name="Paid" />
                                {/* <Bar dataKey="cancelledOrders" fill="#ff4d4f" name="Cancelled" /> */}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </TabPane>

                <TabPane tab="Channels & Modes" key="channels">
                    <Row gutter={[16, 16]}>
                        <Col span={8}>
                            <h4 className="text-center mb-4">Order Sources</h4>
                            <ResponsiveContainer width="100%" height={200}>
                                <PieChart>
                                    <Pie
                                        data={summaryData.orderSource}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={40}
                                        outerRadius={80}
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {summaryData.orderSource.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </Col>
                        <Col span={8}>
                            <h4 className="text-center mb-4">Order Status</h4>
                            <ResponsiveContainer width="100%" height={200}>
                                <PieChart>
                                    <Pie
                                        data={summaryData.orderStatus}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={40}
                                        outerRadius={80}
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {summaryData.orderStatus.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </Col>
                        <Col span={8}>
                            <h4 className="text-center mb-4">Order Modes</h4>
                            <ResponsiveContainer width="100%" height={200}>
                                <PieChart>
                                    <Pie
                                        data={summaryData.orderMode}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={40}
                                        outerRadius={80}
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {summaryData.orderMode.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </Col>
                    </Row>
                </TabPane>
            </Tabs>

            {/* Summary Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-200">
                <Statistic
                    title="Total Revenue"
                    value={chartData.reduce((sum, day) => sum + day.totalRevenue, 0)}
                    formatter={value => `Rs. ${value?.toLocaleString('en-PK')}`}
                />
                <Statistic
                    title="Total Orders"
                    value={chartData.reduce((sum, day) => sum + day.totalOrders, 0)}
                />
                <Statistic
                    title="Success Rate"
                    value={(
                        (chartData.reduce((sum, day) => sum + day.confirmedOrders + day.paidOrders, 0) /
                            chartData.reduce((sum, day) => sum + day.totalOrders, 0)) * 100 || 0
                    ).toFixed(1)}
                    suffix="%"
                />
                <Statistic
                    title="Avg Order Value"
                    value={(
                        chartData.reduce((sum, day) => sum + day.totalRevenue, 0) /
                        chartData.reduce((sum, day) => sum + day.totalOrders, 1)
                    ).toFixed(0)}
                    formatter={value => `Rs. ${value}`}
                />
            </div>
        </Card>
    );
};

export default AdvancedComprehensiveChart;